<script setup>
import LoginInput from './components/LoginInput'
</script>

<template>
    <main class="login-page">
        <section class="brand-panel" aria-label="星铭 IM 品牌介绍">
            <div class="brand-heading">
                <img class="brand-logo" src="@/assets/logo.png" alt="星铭 IM Logo" />
                <div class="brand-title-wrap">
                    <h1>星铭 IM</h1>
                    <span class="brand-line" aria-hidden="true"></span>
                    <p>连接每一次真诚沟通</p>
                </div>
            </div>
            <div class="brand-slogan">
                <span class="sparkle" aria-hidden="true">✦</span>
                <strong>让沟通更简单</strong>
                <span class="paw" aria-hidden="true">●</span>
            </div>
        </section>

        <section class="login-card" aria-labelledby="login-title">
            <div class="card-heading">
                <h2 id="login-title">欢迎登录</h2>
                <p>登录后开启高效沟通</p>
            </div>
            <LoginInput />
        </section>
    </main>
</template>

<style lang="scss" scoped>
.login-page {
    --primary: #0dc8c1;
    --primary-dark: #05afa9;
    position: relative;
    width: 100vw;
    min-width: 320px;
    min-height: 620px;
    height: 100vh;
    overflow: hidden;
    color: #0b173c;
    background: #eaf7ff url('@/assets/im-login-background.png') center center / cover no-repeat;
}

.brand-panel {
    position: absolute;
    top: 6.8%;
    left: max(5.8%, calc(50% - 740px));
    user-select: none;
}

.brand-heading {
    display: flex;
    align-items: center;
}

.brand-logo {
    width: clamp(120px, 9.2vw, 154px);
    height: clamp(120px, 9.2vw, 154px);
    object-fit: contain;
    filter: drop-shadow(0 5px 8px rgba(24, 49, 85, 0.16));
}

.brand-title-wrap {
    margin-left: 18px;

    h1 {
        margin: 0;
        font-size: clamp(42px, 4vw, 66px);
        line-height: 1.06;
        font-weight: 800;
        letter-spacing: 2px;
        color: #071539;
        white-space: nowrap;
        text-shadow: 0 2px 2px rgba(255, 255, 255, 0.9);
    }

    p {
        margin: 11px 0 0;
        font-size: clamp(19px, 1.65vw, 28px);
        line-height: 1.4;
        font-weight: 500;
        letter-spacing: 4px;
        color: #5874a5;
        white-space: nowrap;
    }
}

.brand-line {
    display: block;
    width: 168px;
    height: 4px;
    margin-top: 15px;
    border-radius: 4px;
    background: linear-gradient(90deg, #0dc8c1 0 36%, #80deda 36% 55%, rgba(255, 255, 255, 0.32) 55%);
}

.brand-slogan {
    display: flex;
    align-items: center;
    gap: 18px;
    margin-top: 48px;
    margin-left: 54px;
    color: #078c95;
    transform: rotate(-4deg);

    strong {
        padding-bottom: 8px;
        border-bottom: 4px solid #12bdb7;
        font-family: 'KaiTi', 'STKaiti', 'Microsoft YaHei', sans-serif;
        font-size: clamp(27px, 2.5vw, 41px);
        line-height: 1.2;
        font-weight: 500;
        letter-spacing: 5px;
        white-space: nowrap;
    }

    .sparkle {
        font-size: 38px;
        color: #23d5cb;
    }

    .paw {
        font-size: 27px;
        color: #18cbc2;
    }
}

.login-card {
    position: absolute;
    top: 50%;
    right: max(5.2%, calc(50% - 740px));
    width: min(560px, 38vw);
    min-width: 480px;
    padding: 56px 54px 38px;
    transform: translateY(-50%);
    border: 1px solid rgba(108, 205, 231, 0.58);
    border-radius: 26px;
    background: rgba(255, 255, 255, 0.93);
    box-shadow: 0 18px 48px rgba(43, 110, 169, 0.15);
    backdrop-filter: blur(8px);
    box-sizing: border-box;
}

.card-heading {
    margin-bottom: 26px;

    h2 {
        margin: 0 0 13px;
        font-size: 40px;
        line-height: 1.2;
        font-weight: 800;
        letter-spacing: 2px;
        color: #09163a;
    }

    p {
        margin: 0;
        font-size: 20px;
        line-height: 1.4;
        color: #8295bc;
    }
}

@media (max-width: 1180px) {
    .brand-panel {
        left: 3.5%;
        transform: scale(0.82);
        transform-origin: left top;
    }

    .login-card {
        right: 3.5%;
        width: 460px;
        min-width: 0;
        padding-right: 42px;
        padding-left: 42px;
    }
}

@media (max-width: 900px) {
    .login-page {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 30px 20px;
        background-position: 42% center;
        box-sizing: border-box;
    }

    .brand-panel {
        display: none;
    }

    .login-card {
        position: relative;
        top: auto;
        right: auto;
        width: min(100%, 500px);
        padding: 42px 42px 34px;
        transform: none;
    }
}

@media (max-width: 520px) {
    .login-page {
        min-height: 580px;
        padding: 18px 14px;
    }

    .login-card {
        padding: 32px 24px 27px;
        border-radius: 20px;
    }

    .card-heading {
        margin-bottom: 22px;

        h2 {
            font-size: 31px;
        }

        p {
            font-size: 17px;
        }
    }
}

/* 矮视口（笔记本小屏、系统缩放后高度不足）：压缩卡片纵向留白，保证完整显示 */
@media (min-width: 901px) and (max-height: 760px) {
    .login-card {
        padding: 34px 54px 26px;
    }

    .card-heading {
        margin-bottom: 18px;
    }
}

/* 极矮视口：进一步收拢卡片高度并放宽页面最小高度 */
@media (min-width: 901px) and (max-height: 560px) {
    .login-page {
        min-height: 500px;
    }

    .login-card {
        padding: 26px 54px 20px;
    }

    .card-heading {
        margin-bottom: 12px;

        h2 {
            font-size: 34px;
        }

        p {
            font-size: 18px;
        }
    }
}

/* 横屏矮视口（手机横屏等）：卡片改为紧凑布局，减少页面滚动 */
@media (max-width: 900px) and (max-height: 560px) {
    .login-page {
        min-height: 480px;
        padding: 16px;
    }

    .login-card {
        padding: 24px 34px 18px;
    }

    .card-heading {
        margin-bottom: 12px;

        h2 {
            font-size: 28px;
        }

        p {
            font-size: 16px;
        }
    }
}
</style>
